"use client"

import React, { useState, useRef } from "react"
import { motion, AnimatePresence } from "framer-motion"
import "../styles/CircularLinkedList.css"

function CircularLinkedList({ addLog }) {
  const [seats, setSeats] = useState([])
  const [inputValue, setInputValue] = useState("")
  const [inputPosition, setInputPosition] = useState("")
  const [isAnimating, setIsAnimating] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(null)
  const [animationSpeed, setAnimationSpeed] = useState(500)
  const [currentOperation, setCurrentOperation] = useState(null)
  const [searchValue, setSearchValue] = useState("")
  const [searchResult, setSearchResult] = useState(null)
  const [isRotating, setIsRotating] = useState(false)
  const [rotationSpeed, setRotationSpeed] = useState(10)
  const [currentPosition, setCurrentPosition] = useState(0)

  const ferrisWheelRef = useRef(null)

  // Generate a random color for ferris wheel seats
  const getRandomColor = () => {
    const colors = ["#FF6B6B", "#4ECDC4", "#FFD166", "#06D6A0", "#118AB2", "#EF476F", "#FFC43D", "#1B9AAA"]
    return colors[Math.floor(Math.random() * colors.length)]
  }

  // Add a seat at a specific position
  const handleAddSeat = () => {
    if (inputValue.trim() === "") return
    if (isAnimating) return

    let position = 0
    if (inputPosition.trim() !== "") {
      position = Number.parseInt(inputPosition)
      if (isNaN(position) || position < 0 || position > seats.length) {
        addLog(`Invalid position: ${inputPosition}. Must be between 0 and ${seats.length}`, "error")
        return
      }
    }

    setIsAnimating(true)
    setCurrentOperation("add")

    const newSeat = {
      id: Date.now(),
      value: inputValue,
      color: getRandomColor(),
      isNew: true,
    }

    addLog(`Adding passenger "${inputValue}" at position ${position} of the Ferris wheel`, "success")

    // First show the new seat with animation
    setSeats((prevSeats) => {
      const updatedSeats = [...prevSeats]
      updatedSeats.splice(position, 0, { ...newSeat, isFloating: true })
      return updatedSeats
    })

    // Then after a delay, place it in position
    setTimeout(() => {
      setSeats((prevSeats) => {
        const updatedSeats = prevSeats.map((seat, index) =>
          index === position ? { ...seat, isFloating: false, isNew: false } : seat,
        )
        return updatedSeats
      })

      setTimeout(() => {
        setIsAnimating(false)
        setCurrentOperation(null)
        setInputValue("")
        setInputPosition("")
        setCurrentPosition(position)
      }, animationSpeed)
    }, animationSpeed)
  }

  // Delete a seat at a specific position
  const handleDelete = () => {
    if (inputPosition.trim() === "") return
    if (isAnimating) return
    if (seats.length === 0) {
      addLog("Cannot delete from an empty Ferris wheel", "error")
      return
    }

    const position = Number.parseInt(inputPosition)
    if (isNaN(position) || position < 0 || position >= seats.length) {
      addLog(`Invalid position: ${inputPosition}. Must be between 0 and ${seats.length - 1}`, "error")
      return
    }

    setIsAnimating(true)
    setCurrentOperation("delete")

    addLog(`Removing passenger at position ${position} from the Ferris wheel`, "info")

    // Rotate to the position
    setCurrentPosition(position)

    // Mark the seat as being deleted
    setTimeout(() => {
      setSeats((prevSeats) => {
        const updatedSeats = [...prevSeats]
        updatedSeats[position] = { ...updatedSeats[position], isDeleting: true }
        return updatedSeats
      })

      // After animation, remove the seat
      setTimeout(() => {
        setSeats((prevSeats) => {
          const updatedSeats = [...prevSeats]
          updatedSeats.splice(position, 1)
          return updatedSeats
        })

        // Reset currentPosition if all seats are removed
        setCurrentPosition((prevPosition) => {
          if (seats.length <= 1) {
            return 0
          }
          return Math.min(prevPosition, seats.length - 2)
        })

        setIsAnimating(false)
        setCurrentOperation(null)
        setInputPosition("")
      }, animationSpeed)
    }, animationSpeed)
  }

  // Search for a seat with a specific value
  const handleSearch = () => {
    if (searchValue.trim() === "") return
    if (isAnimating) return
    if (seats.length === 0) {
      addLog("Cannot search in an empty Ferris wheel", "error")
      return
    }

    setIsAnimating(true)
    setCurrentOperation("search")
    setSearchResult(null)

    addLog(`Searching for passenger "${searchValue}" in the Ferris wheel`, "info")

    // Simulate search
    const searchSeats = async () => {
      let found = false

      for (let i = 0; i < seats.length; i++) {
        setCurrentPosition(i)
        setHighlightedIndex(i)
        await new Promise((resolve) => setTimeout(resolve, animationSpeed))

        if (seats[i].value.toLowerCase() === searchValue.toLowerCase()) {
          found = true
          setSearchResult({ index: i, value: searchValue, found: true })
          addLog(`Found passenger "${searchValue}" at position ${i}`, "success")
          break
        }
      }

      if (!found) {
        setSearchResult({ found: false, value: searchValue })
        addLog(`Passenger "${searchValue}" not found in the Ferris wheel`, "warning")
      }

      setTimeout(() => {
        setHighlightedIndex(null)
        setIsAnimating(false)
        setCurrentOperation(null)
        setSearchValue("")
      }, animationSpeed)
    }

    searchSeats()
  }

  // Toggle rotation of the Ferris wheel
  const toggleRotation = () => {
    setIsRotating(!isRotating)
    addLog(isRotating ? "Ferris wheel stopped" : "Ferris wheel started rotating", "info")
  }

  // Move to the next position (simulate traversal)
  const handleTraverse = () => {
    if (isAnimating || seats.length === 0) return

    setIsAnimating(true)
    addLog("Traversing the circular linked list...", "info")

    const traverseSeats = async () => {
      for (let i = 0; i < seats.length; i++) {
        const position = (currentPosition + i) % seats.length
        setCurrentPosition(position)
        setHighlightedIndex(position)
        addLog(`Visited passenger "${seats[position].value}" at position ${position}`, "info")
        await new Promise((resolve) => setTimeout(resolve, animationSpeed))
      }

      // Return to the starting position to demonstrate circularity
      setCurrentPosition(currentPosition)
      setHighlightedIndex(null)
      setIsAnimating(false)
      addLog("Traversal complete - returned to starting position", "success")
    }

    traverseSeats()
  }

  // Clear the entire Ferris wheel
  const handleClear = () => {
    if (seats.length === 0) return

    setSeats([])
    setHighlightedIndex(null)
    setSearchResult(null)
    setCurrentPosition(0)
    setIsRotating(false)
    addLog("Cleared all passengers from the Ferris wheel", "warning")
  }

  // Calculate position for each seat based on its index
  const getSeatPosition = (index, totalSeats) => {
    if (totalSeats === 0) return { x: 0, y: 0 }

    const radius = 150
    const angle = (index * (360 / totalSeats) + (isRotating ? currentPosition * 10 : 0)) % 360
    const radians = (angle * Math.PI) / 180

    return {
      x: radius * Math.cos(radians),
      y: radius * Math.sin(radians),
    }
  }

  return (
    <div className="circular-linked-list">
      <div className="list-controls">
        <div className="input-group">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Enter passenger name"
            disabled={isAnimating}
            className="seat-input"
          />

          <div className="position-input">
            <input
              type="text"
              value={inputPosition}
              onChange={(e) => setInputPosition(e.target.value)}
              placeholder="Position (optional)"
              disabled={isAnimating}
              className="position-input"
            />
          </div>

          <div className="operation-buttons">
            <button onClick={handleAddSeat} disabled={isAnimating || inputValue.trim() === ""} className="add-button">
              Add Passenger
            </button>

            <button
              onClick={handleDelete}
              disabled={isAnimating || inputPosition.trim() === "" || seats.length === 0}
              className="delete-button"
            >
              Remove Passenger
            </button>

            <button onClick={handleTraverse} disabled={isAnimating || seats.length === 0} className="traverse-button">
              Traverse
            </button>
          </div>
        </div>

        <div className="search-group">
          <input
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Search passenger"
            disabled={isAnimating}
            className="search-input"
          />

          <button
            onClick={handleSearch}
            disabled={isAnimating || searchValue.trim() === "" || seats.length === 0}
            className="search-button"
          >
            Search
          </button>

          <button
            onClick={toggleRotation}
            disabled={isAnimating || seats.length === 0}
            className={`rotate-button ${isRotating ? "active" : ""}`}
          >
            {isRotating ? "Stop Rotation" : "Start Rotation"}
          </button>

          <button onClick={handleClear} disabled={isAnimating || seats.length === 0} className="clear-button">
            Clear Wheel
          </button>
        </div>
      </div>

      <div className="ferris-wheel-visualization" ref={ferrisWheelRef}>
        {seats.length === 0 ? (
          <div className="empty-wheel-message">The Ferris wheel is empty. Add some passengers!</div>
        ) : (
          <div className={`ferris-wheel ${isRotating ? "rotating" : ""}`}>
            <div className="wheel-center"></div>

            <div className="wheel-structure">
              {Array(8)
                .fill()
                .map((_, i) => (
                  <div
                    key={i}
                    className="wheel-spoke"
                    style={{
                      transform: `rotate(${i * 45}deg)`,
                    }}
                  ></div>
                ))}

              <div className="wheel-rim"></div>
            </div>

            <AnimatePresence>
              {seats.map((seat, index) => {
                const position = getSeatPosition(index, seats.length)
                const nextIndex = (index + 1) % seats.length
                const nextPosition = getSeatPosition(nextIndex, seats.length)

                // Calculate the arrow position and angle
                const arrowPosition = {
                  x: (position.x + nextPosition.x) / 2,
                  y: (position.y + nextPosition.y) / 2,
                }

                // Calculate angle for the arrow
                const angle = (Math.atan2(nextPosition.y - position.y, nextPosition.x - position.x) * 180) / Math.PI

                return (
                  <React.Fragment key={seat.id}>
                    <motion.div
                      className={`wheel-seat ${highlightedIndex === index ? "highlighted" : ""} ${seat.isNew ? "new-seat" : ""}`}
                      style={{
                        backgroundColor: seat.color,
                        zIndex: highlightedIndex === index ? 10 : 1,
                      }}
                      initial={
                        seat.isFloating
                          ? { x: 0, y: -200, opacity: 0.7 }
                          : {
                              x: position.x,
                              y: position.y,
                              opacity: 1,
                            }
                      }
                      animate={
                        seat.isDeleting
                          ? { x: 0, y: 200, opacity: 0, transition: { duration: 0.5 } }
                          : seat.isFloating
                            ? { x: 0, y: -200, opacity: 0.7 }
                            : {
                                x: position.x,
                                y: position.y,
                                opacity: 1,
                                transition: {
                                  duration: isRotating ? 0.1 : 0.5,
                                  ease: "linear",
                                },
                              }
                      }
                      exit={{ y: 200, opacity: 0, transition: { duration: 0.5 } }}
                    >
                      <div className="seat-content">
                        <div className="seat-value">{seat.value}</div>
                        <div className="seat-index">{index}</div>
                        {searchResult && searchResult.found && searchResult.index === index && (
                          <div className="search-indicator">Found!</div>
                        )}
                      </div>
                      <div className="seat-connector"></div>
                    </motion.div>

                    {/* Add connection arrow between seats */}
                    {seats.length > 1 && (
                      <motion.div
                        className="wheel-connection"
                        style={{
                          x: arrowPosition.x,
                          y: arrowPosition.y,
                          rotate: angle,
                          opacity: highlightedIndex === index ? 1 : 0.6,
                        }}
                        animate={{
                          x: arrowPosition.x,
                          y: arrowPosition.y,
                          rotate: angle,
                          opacity: highlightedIndex === index ? 1 : 0.6,
                          transition: {
                            duration: isRotating ? 0.1 : 0.5,
                            ease: "linear",
                          },
                        }}
                      >
                        <div className="connection-arrow">→</div>
                      </motion.div>
                    )}
                  </React.Fragment>
                )
              })}
            </AnimatePresence>

            <div className="wheel-support">
              <div className="support-leg left"></div>
              <div className="support-leg right"></div>
              <div className="support-base"></div>
            </div>
          </div>
        )}

        {searchResult && searchResult.found === false && (
          <div className="search-not-found">Passenger "{searchResult.value}" not found in the Ferris wheel</div>
        )}
      </div>

      <div className="list-info">
        <div className="list-status">
          <div className="status-item">
            <span className="status-label">Length:</span>
            <span className="status-value">{seats.length}</span>
          </div>
          <div className="status-item">
            <span className="status-label">Current:</span>
            <span className="status-value">
              {seats.length > 0 ? seats[currentPosition < seats.length ? currentPosition : 0].value : "null"}
            </span>
          </div>
          <div className="status-item">
            <span className="status-label">Next:</span>
            <span className="status-value">
              {seats.length > 0 ? seats[(currentPosition + 1) % seats.length].value : "null"}
            </span>
          </div>
        </div>

        <div className="animation-controls">
          <div className="animation-speed">
            <label>Animation Speed:</label>
            <input
              type="range"
              min="200"
              max="1000"
              step="100"
              value={animationSpeed}
              onChange={(e) => setAnimationSpeed(Number(e.target.value))}
              disabled={isAnimating}
            />
            <span>{animationSpeed}ms</span>
          </div>

          <div className="rotation-speed">
            <label>Rotation Speed:</label>
            <input
              type="range"
              min="1"
              max="20"
              step="1"
              value={rotationSpeed}
              onChange={(e) => setRotationSpeed(Number(e.target.value))}
              disabled={isAnimating}
            />
            <span>{rotationSpeed}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CircularLinkedList
